import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Target, Heart, Award, Users } from 'lucide-react';

export const ValuesSlider = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const values = [
    {
      icon: <Target className="h-8 w-8" />,
      title: 'Excellence',
      description: 'We are committed to delivering exceptional service and results in every trademark registration and legal service we provide.',
    },
    {
      icon: <Heart className="h-8 w-8" />,
      title: 'Client-Focused',
      description: 'Your success is our priority. We take time to understand your unique needs and provide personalized solutions.',
    },
    {
      icon: <Award className="h-8 w-8" />,
      title: 'Expertise',
      description: 'Our team brings years of experience in trademark law and intellectual property protection to serve you better.',
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: 'Partnership',
      description: 'We build lasting relationships with our clients, supporting them throughout their brand protection journey.',
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % values.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [values.length]);

  return (
    <div className="relative" data-testid="values-slider">
      {/* Desktop View - All Values in Grid */}
      <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-8">
        {values.map((value, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 hover:shadow-lg transition-all duration-300"
          >
            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl w-16 h-16 flex items-center justify-center text-red-700 mb-6">
              {value.icon}
            </div>
            <h3 className="font-heading font-bold text-xl text-slate-900 mb-3">
              {value.title}
            </h3>
            <p className="text-slate-600 leading-relaxed">
              {value.description}
            </p>
          </motion.div>
        ))}
      </div>

      {/* Mobile View - Slider */}
      <div className="md:hidden">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -100 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 hover:shadow-lg transition-all duration-300"
        >
          <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl w-16 h-16 flex items-center justify-center text-red-700 mb-6">
            {values[currentIndex].icon}
          </div>
          <h3 className="font-heading font-bold text-xl text-slate-900 mb-3">
            {values[currentIndex].title}
          </h3>
          <p className="text-slate-600 leading-relaxed">
            {values[currentIndex].description}
          </p>
        </motion.div>

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-6">
          {values.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentIndex ? 'w-8 bg-red-600' : 'w-2 bg-slate-300'
              }`}
              data-testid={`values-dot-${index}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
